import React, { Dispatch, SetStateAction } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { FormField } from '../../FormField/FormField';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import { ExerciseItem } from '../exercises/ExerciseItem';
import { DayItemSharedProps } from '../types';
import { DayItem } from './DayItem';
import { ExerciseFilters } from './ExerciseFilters';

type ExerciseDaysFieldProps = DayItemSharedProps & {
  onDragEnd: (result: DropResult) => void;
  setSelectedExerciseMuscleGroups: Dispatch<SetStateAction<MUSCLE_GROUPS[]>>;
  setSelectedExerciseCategories: Dispatch<SetStateAction<EXERCISE_CATEGORIES[]>>;
};

export const ExerciseDaysField = ({
  onDragEnd,
  setSelectedExerciseMuscleGroups,
  setSelectedExerciseCategories,
  ...rowProps
}: ExerciseDaysFieldProps) => {
  const { form, isEditing, isCreating, validationErrors, days, selectedExerciseMuscleGroups, selectedExerciseCategories } =
    rowProps;

  return (
    <FormField
      labelTitle='Días de ejercicios'
      fullWidth
      error={typeof validationErrors?.exerciseDays === 'string' ? validationErrors.exerciseDays : undefined}
      dontShowErrorMessage
      required={isCreating || isEditing}
    >
      <div className='exercise-days-container'>
        {isEditing || isCreating ? (
          <>
            {form.exerciseDays.length > 0 ? (
              <DragDropContext
                onDragStart={() => {
                  // Añadir clase al body durante el arrastre
                  document.body.classList.add('rbd-dragging');

                  // Nota: No modificamos el estado de acordeones expandidos aquí
                  // Lo manejaremos correctamente en onDragEnd para preservar
                  // el estado de los acordeones y mapearlo a las nuevas posiciones
                }}
                onDragEnd={(result) => {
                  // Quitar clase del body después del arrastre
                  document.body.classList.remove('rbd-dragging');
                  onDragEnd(result);
                }}
              >
                {/* Lista vertical para los días */}
                {(isEditing || isCreating) && (
                  <ExerciseFilters
                    selectedExerciseMuscleGroups={selectedExerciseMuscleGroups}
                    setSelectedExerciseMuscleGroups={setSelectedExerciseMuscleGroups}
                    selectedExerciseCategories={selectedExerciseCategories}
                    setSelectedExerciseCategories={setSelectedExerciseCategories}
                  />
                )}
                <div className='exercise-days-edit-container'>
                  <Droppable droppableId='days-list' type='days' direction='horizontal' ignoreContainerClipping>
                    {(provided, snapshot) => {
                      const isDragging = snapshot.isDraggingOver;

                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={classNames('exercise-days-view-grid', { 'is-dragging-over': isDragging })}
                        >
                          {form.exerciseDays.map((day, dayIndex) => (
                            <Draggable
                              key={day.id || `day-${day.dayNumber}`}
                              draggableId={day.id || `day-${day.dayNumber}`}
                              index={dayIndex}
                            >
                              {(provided, snapshot) => {
                                const style = {
                                  ...provided.draggableProps.style,
                                };

                                return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={style}
                                    className={classNames('exercise-day-draggable', { 'is-dragging': snapshot.isDragging })}
                                  >
                                    <DayItem {...rowProps} day={day} dragHandleProps={provided.dragHandleProps} />
                                  </div>
                                );
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                </div>
              </DragDropContext>
            ) : (
              <div className={classNames('no-days-message', { 'invalid-form-field': !!validationErrors?.exerciseDays })}>
                <i className={PrimeIcons.EXCLAMATION_CIRCLE} />
                <p>Debes añadir al menos un día de ejercicios</p>
              </div>
            )}
          </>
        ) : (
          <div className='exercise-days-view-grid'>
            {form.exerciseDays?.map((day, dayIndex) => (
              <div key={day.dayNumber} className='exercise-day-item'>
                <div className='exercise-day-header'>
                  <div className='day-info'>
                    <span className='day-number'>{day.name || `Día ${day.dayNumber}`}</span>
                    <span className='exercise-count'>
                      {day.exercises.length} ejercicio{day.exercises.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className='exercise-day-content'>
                  {day.exercises.length > 0 ? (
                    <div className='exercise-list'>
                      {day.exercises.map((exercise, exerciseIndex) => (
                        <div key={`${day.dayNumber}-${exerciseIndex}`}>
                          <ExerciseItem {...rowProps} exercise={exercise} dayIndex={dayIndex} exerciseIndex={exerciseIndex} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='no-exercises'>
                      <i className={PrimeIcons.INFO_CIRCLE} />
                      <span>No hay ejercicios asignados para este día</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {(isEditing || isCreating) && (
          <Button
            label='Añadir día'
            icon='pi pi-plus'
            onClick={days.handleAddDay}
            text={form.exerciseDays.length > 0}
            className={classNames('add-day-button', { 'p-button-outlined': form.exerciseDays.length === 0 })}
          />
        )}
      </div>
    </FormField>
  );
};
